/**
 * Redux slice for network status management
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../../app/store';
import type { 
  NetworkStatusState, 
  NetworkStatusResponse,
  NetworkStatusData 
} from '../../types/networkStatus';
import { ALL_EXCHANGE_INDICES } from '../../types/networkStatus';
import { networkStatusService } from '../../services/networkStatusService';
import { networkStatusStorage } from '../../services/networkStatusStorage';

// Initial state with array for data (12 exchanges, indices 0-11)
const initialState: NetworkStatusState = {
  data: new Array(12), // Array with 12 slots for exchanges 0-11
  loading: {},
  error: {},
  lastFetchTime: {},
  autoRefreshEnabled: true,
};

// Initialize loading and error states for all exchanges
ALL_EXCHANGE_INDICES.forEach(exchange => {
  initialState.loading[exchange] = false;
  initialState.error[exchange] = null;
  initialState.lastFetchTime[exchange] = 0;
});

// Async thunks
export const fetchNetworkStatus = createAsyncThunk(
  'networkStatus/fetch',
  async (exchange: number) => {
    const response = await networkStatusService.fetchExchangeNetworkStatus(exchange);
    if (!response) {
      throw new Error(`Failed to fetch network status for ${exchange}`);
    }
    return response;
  }
);

export const fetchAllNetworkStatus = createAsyncThunk(
  'networkStatus/fetchAll',
  async () => {
    const results = await networkStatusService.fetchAllExchanges();
    const data: NetworkStatusData = new Array(12);
    
    results.forEach((response, exchange) => {
      if (response) {
        data[exchange] = response;
      }
    });
    
    // Save to storage after successful fetch
    await networkStatusStorage.save(data);
    
    return data;
  }
);

export const loadFromStorage = createAsyncThunk(
  'networkStatus/loadFromStorage',
  async () => {
    const stored = await networkStatusStorage.load();
    
    if (!stored) {
      return null;
    }
    
    // Check if migration is needed
    if (stored.version < 1) {
      const migrated = await networkStatusStorage.migrate(stored);
      if (migrated) {
        await networkStatusStorage.save(migrated.data);
        return migrated;
      }
    }
    
    return stored;
  }
);

export const refreshNetworkStatus = createAsyncThunk(
  'networkStatus/refresh',
  async (exchange: number | undefined = undefined) => {
    if (exchange !== undefined) {
      // Refresh single exchange
      const response = await networkStatusService.fetchExchangeNetworkStatus(exchange);
      if (!response) {
        throw new Error(`Failed to refresh network status for ${exchange}`);
      }
      return { exchange, response };
    } else {
      // Refresh all exchanges
      const results = await networkStatusService.fetchAllExchanges();
      const data: NetworkStatusData = new Array(12);
      
      results.forEach((response, exchange) => {
        if (response) {
          data[exchange] = response;
        }
      });
      
      // Save to storage after successful refresh
      await networkStatusStorage.save(data);
      
      return { data };
    }
  }
);

// Alias for single exchange refresh for convenience
export const fetchSingleExchangeNetworkStatus = (exchange: number) => refreshNetworkStatus(exchange);

// Create slice
const networkStatusSlice = createSlice({
  name: 'networkStatus',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<{ exchange: number; loading: boolean }>) => {
      state.loading[action.payload.exchange] = action.payload.loading;
    },
    setError: (state, action: PayloadAction<{ exchange: number; error: string | null }>) => {
      state.error[action.payload.exchange] = action.payload.error;
    },
    toggleAutoRefresh: (state) => {
      state.autoRefreshEnabled = !state.autoRefreshEnabled;
    },
    setAutoRefresh: (state, action: PayloadAction<boolean>) => {
      state.autoRefreshEnabled = action.payload;
    },
    updateNetworkStatus: (state, action: PayloadAction<NetworkStatusResponse>) => {
      // Update single network status from WebSocket
      const { exchange, cryptos, timestamp } = action.payload;
      // Store directly in array at the exchange index
      state.data[exchange] = action.payload;
      state.lastFetchTime[exchange] = timestamp || Date.now();
      state.error[exchange] = null;
      state.loading[exchange] = false;
    },
    updateNetworkStatusBatch: (state, action: PayloadAction<NetworkStatusResponse[]>) => {
      // Update multiple network statuses from WebSocket (initial data)
      action.payload.forEach(status => {
        state.data[status.exchange] = status;
        state.lastFetchTime[status.exchange] = status.timestamp || Date.now();
        state.error[status.exchange] = null;
        state.loading[status.exchange] = false;
      });
    },
    clearNetworkStatus: (state) => {
      state.data = new Array(12); // Reset to empty array
      ALL_EXCHANGE_INDICES.forEach(exchange => {
        state.lastFetchTime[exchange] = 0;
        state.error[exchange] = null;
      });
    },
  },
  extraReducers: (builder) => {
    // fetchNetworkStatus
    builder
      .addCase(fetchNetworkStatus.pending, (state, action) => {
        const exchange = action.meta.arg;
        state.loading[exchange] = true;
        state.error[exchange] = null;
      })
      .addCase(fetchNetworkStatus.fulfilled, (state, action) => {
        const exchange = action.meta.arg;
        state.loading[exchange] = false;
        
        // Validate the payload before storing
        if (action.payload && Array.isArray(action.payload.cryptos)) {
          state.data[exchange] = action.payload;
          state.lastFetchTime[exchange] = Date.now();
          state.error[exchange] = null;
        } else {
          console.error(`Invalid network status data received for exchange ${exchange}:`, action.payload);
          state.error[exchange] = 'Invalid data format received';
        }
      })
      .addCase(fetchNetworkStatus.rejected, (state, action) => {
        const exchange = action.meta.arg;
        state.loading[exchange] = false;
        state.error[exchange] = action.error.message || 'Failed to fetch network status';
      });

    // fetchAllNetworkStatus
    builder
      .addCase(fetchAllNetworkStatus.pending, (state) => {
        ALL_EXCHANGE_INDICES.forEach(exchange => {
          state.loading[exchange] = true;
          state.error[exchange] = null;
        });
      })
      .addCase(fetchAllNetworkStatus.fulfilled, (state, action) => {
        // Validate all exchange data before storing
        const validatedData: NetworkStatusData = new Array(12);
        
        ALL_EXCHANGE_INDICES.forEach(exchange => {
          state.loading[exchange] = false;
          const exchangeData = action.payload[exchange];
          
          if (exchangeData && Array.isArray(exchangeData.cryptos)) {
            validatedData[exchange] = exchangeData;
            state.lastFetchTime[exchange] = Date.now();
            state.error[exchange] = null;
          } else if (exchangeData) {
            console.error(`Invalid data format for exchange ${exchange}:`, exchangeData);
            state.error[exchange] = 'Invalid data format';
          } else {
            state.error[exchange] = 'No data available';
          }
        });
        
        state.data = validatedData;
      })
      .addCase(fetchAllNetworkStatus.rejected, (state, action) => {
        ALL_EXCHANGE_INDICES.forEach(exchange => {
          state.loading[exchange] = false;
          state.error[exchange] = action.error.message || 'Failed to fetch network status';
        });
      });

    // loadFromStorage
    builder
      .addCase(loadFromStorage.fulfilled, (state, action) => {
        if (action.payload) {
          // Validate the data before storing
          const validatedData: NetworkStatusData = new Array(12);
          
          ALL_EXCHANGE_INDICES.forEach(exchange => {
            const exchangeData = action.payload.data[exchange];
            if (exchangeData && Array.isArray(exchangeData.cryptos)) {
              validatedData[exchange] = exchangeData;
              state.lastFetchTime[exchange] = action.payload.timestamp;
            }
          });
          
          state.data = validatedData;
        }
      });

    // refreshNetworkStatus
    builder
      .addCase(refreshNetworkStatus.pending, (state, action) => {
        if (action.meta.arg) {
          // Single exchange refresh
          state.loading[action.meta.arg] = true;
          state.error[action.meta.arg] = null;
        } else {
          // All exchanges refresh
          ALL_EXCHANGE_INDICES.forEach(exchange => {
            state.loading[exchange] = true;
            state.error[exchange] = null;
          });
        }
      })
      .addCase(refreshNetworkStatus.fulfilled, (state, action) => {
        if ('exchange' in action.payload) {
          // Single exchange refresh
          const { exchange, response } = action.payload;
          state.loading[exchange] = false;
          
          // Validate the response before storing
          if (response && Array.isArray(response.cryptos)) {
            state.data[exchange] = response;
            state.lastFetchTime[exchange] = Date.now();
            state.error[exchange] = null;
          } else {
            console.error(`Invalid data format for exchange ${exchange} refresh:`, response);
            state.error[exchange] = 'Invalid data format';
          }
        } else {
          // All exchanges refresh - validate all data
          state.data = new Array(12); // Reset to new array
          
          ALL_EXCHANGE_INDICES.forEach(exchange => {
            state.loading[exchange] = false;
            const exchangeData = action.payload.data[exchange];
            
            if (exchangeData && Array.isArray(exchangeData.cryptos)) {
              state.data[exchange] = exchangeData;
              state.lastFetchTime[exchange] = Date.now();
              state.error[exchange] = null;
            } else if (exchangeData) {
              console.error(`Invalid data format for exchange ${exchange}:`, exchangeData);
              state.error[exchange] = 'Invalid data format';
            } else {
              state.error[exchange] = 'No data available';
            }
          });
        }
      })
      .addCase(refreshNetworkStatus.rejected, (state, action) => {
        if (action.meta.arg) {
          // Single exchange refresh failed
          state.loading[action.meta.arg] = false;
          state.error[action.meta.arg] = action.error.message || 'Failed to refresh network status';
        } else {
          // All exchanges refresh failed
          ALL_EXCHANGE_INDICES.forEach(exchange => {
            state.loading[exchange] = false;
            state.error[exchange] = action.error.message || 'Failed to refresh network status';
          });
        }
      });
  },
});

// Export actions
export const {
  setLoading,
  setError,
  toggleAutoRefresh,
  setAutoRefresh,
  updateNetworkStatus,
  updateNetworkStatusBatch,
  clearNetworkStatus,
} = networkStatusSlice.actions;

// Selectors
export const selectNetworkStatus = (state: RootState) => state.networkStatus.data;
export const selectNetworkStatusByExchange = (exchange: number) => (state: RootState) => 
  state.networkStatus.data[exchange];
export const selectIsLoading = (exchange: number) => (state: RootState) => 
  state.networkStatus.loading[exchange];
export const selectError = (exchange: number) => (state: RootState) => 
  state.networkStatus.error[exchange];
export const selectLastFetchTime = (exchange: number) => (state: RootState) => 
  state.networkStatus.lastFetchTime[exchange];
export const selectAutoRefreshEnabled = (state: RootState) => 
  state.networkStatus.autoRefreshEnabled;
export const selectAllLoadingStates = (state: RootState) => 
  state.networkStatus.loading;
export const selectAnyLoading = (state: RootState) => 
  Object.values(state.networkStatus.loading).some(loading => loading);
export const selectIsStale = (exchange: number) => (state: RootState) => {
  const lastFetch = state.networkStatus.lastFetchTime[exchange];
  if (!lastFetch) return true;
  return networkStatusStorage.isStale(lastFetch);
};

// Export reducer
export default networkStatusSlice.reducer;